import { useMutation } from "@tanstack/react-query";
import { Check, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getPosClient } from "../../lib/api";

interface PinSetupGateProps {
  readonly onPinSet: () => void;
}

const PIN_RE = /^[0-9]{4,6}$/;

/**
 * Full-screen blocking gate shown when the user has no PIN set.
 * All POS users need a PIN so the inactivity lock screen can protect the
 * terminal when they step away (PCI DSS 4.0 Req 8.2.8).
 */
export function PinSetupGate({ onPinSet }: PinSetupGateProps) {
  const { t } = useTranslation();
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const setPin = useMutation({
    mutationFn: (pin: string) => getPosClient().setPin({ new_pin: pin }),
    onSuccess: () => {
      setDone(true);
      setTimeout(onPinSet, 1500);
    },
    onError: () => {
      setError(t("pinGate.errors.saveFailed"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!PIN_RE.test(newPin)) {
      setError(t("pinGate.errors.invalidFormat"));
      return;
    }
    if (newPin !== confirmPin) {
      setError(t("pinGate.errors.confirmMismatch"));
      return;
    }
    setPin.mutate(newPin);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm space-y-6 rounded-2xl border border-line bg-surface p-8 shadow-elev-2">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10">
            <KeyRound className="h-6 w-6 text-brand-primary" aria-hidden />
          </div>
          <h1 className="text-lg font-bold text-strong">{t("pinGate.title")}</h1>
          <p className="text-sm text-muted">{t("pinGate.body")}</p>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <Check className="h-10 w-10 text-emerald-600" aria-hidden />
            <p className="font-semibold text-emerald-700 dark:text-emerald-400">
              {t("pinGate.success")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <PinField
              label={t("pinGate.newLabel")}
              value={newPin}
              onChange={(v) => {
                setNewPin(v);
                setError(null);
              }}
              autoFocus
            />
            <PinField
              label={t("pinGate.confirmLabel")}
              value={confirmPin}
              onChange={(v) => {
                setConfirmPin(v);
                setError(null);
              }}
            />
            {error && (
              <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={newPin.length < 4 || confirmPin.length < 4 || setPin.isPending}
              className="w-full rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {setPin.isPending ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {t("pinGate.saving")}
                </span>
              ) : (
                t("pinGate.confirm")
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

interface PinFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly autoFocus?: boolean;
}

function PinField({ label, value, onChange, autoFocus }: PinFieldProps) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="off"
        maxLength={6}
        autoFocus={autoFocus}
        placeholder="----"
        className="mt-1 block w-full rounded-md bg-surface px-3 py-2 text-center font-mono text-lg tracking-[0.4em] text-strong ring-1 ring-inset ring-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      />
    </label>
  );
}
